import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import "../styles/allmatch.css";
import IMAGES from "../globals/images";
import STRINGS from "../globals/strings";
import endpoints from '../api/endpoints';

function AllMatch() {
    const navigate = useNavigate();
    const [allMatchData, setAllMatchData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState("20");
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();

    const getTeamImageUrl = (inning) => {
        const teamImage = inning.team_image || inning.image;
        const teamName = inning.team_name;
        const teamId = inning.team_id;

        if (teamImage && teamImage.charAt(teamImage.length - 1) === '/') {
            if (teamId) {
                return `${teamImage}${teamId}.png`;
            } else if (teamName) {
                const formattedName = teamName.replace(/\s+/g, '_').toLowerCase();
                return `${teamImage}${formattedName}.png`;
            } else {
                return IMAGES.PHOENIX_LOGO;
            }
        }

        return teamImage || IMAGES.PHOENIX_LOGO;
    };

    const lastMatchElementRef = useCallback(
        (node) => {
            if (isLoading || !hasMore) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoading) {
                    setCurrentPage((prevPage) => prevPage + 1);
                }
            }, { threshold: 0.1 }); 
            if (node) observer.current.observe(node);
        },
        [isLoading, hasMore]
    );

    useEffect(() => {
        if (!hasMore || isLoading) return;
        fetchMatches(currentPage);
    }, [currentPage, hasMore]); 

    const fetchMatches = async (page) => {
        setIsLoading(true);
        try {
            // console.log(`Fetching page ${page} with perPage ${perPage}`);
            const result = await endpoints.getMatchList("0", page.toString(), perPage);
            // console.log('API Response:', result);
            if (result.success !== false && Array.isArray(result.data)) {
                setAllMatchData((prevData) => {
                  
                    const newData = result.data.filter(
                        (newMatch) => !prevData.some((prevMatch) => prevMatch.match_id === newMatch.match_id)
                    );
                    return [...prevData, ...newData];
                });
                setHasMore(result.data.length === parseInt(perPage));
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Fetch matches failed:", error);
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    };

    const formatOversLabel = (overs) => {
        if (overs == null || overs === 0) {
            return "Yet to bat...";
        }
        const formatted = Number(overs).toFixed(1).replace(/\.0$/, '');
        return `(Over ${formatted})`;
    };

    const handleClick = (isLive, match_id, is_quickMatch) => {
        isLive ? navigate(`/liveMatch/${match_id}/${is_quickMatch ? 'qm' : 'rm'}`) : navigate(`/${match_id}/${is_quickMatch ? 'qm' : 'rm'}`);
    };

    const LoadMoreLoader = () => (
        <div className="load-more-loader">
            <span>...No More Matches...</span>
        </div>
    );

    return (
        <div className='all-match'>
            <div className="all-match-div">
                <span className="all-match-text">All Matches</span>
            </div>
            {allMatchData.map((match, index) => {
                const isOngoing = match.match_status === 'Ongoing';
                const isSecondInningYetToBat = match.second_ining.overs == null || match.second_ining.overs === 0;
                return (
                    <div
                        key={`${match.match_id}-${index}`}
                        ref={index === allMatchData.length - 1 ? lastMatchElementRef : null}
                        className="match-card"
                        onClick={() => handleClick(isOngoing, match.match_id, match.is_quick_match)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="match-header">
                            <span className="header-text">{match.match_type}</span>
                            <div
                                className="status-tab"
                                style={{ backgroundImage: `url(${isOngoing ? IMAGES.BGGREEN : IMAGES.BGBROWN})` }}
                            >
                                <img src={IMAGES.ONGOINGICON} alt={STRINGS.IMAGEALT} className="status-icon" />
                                <span className="status-text">{isOngoing ? 'Ongoing' : 'View Result'}</span>
                            </div>
                            <span className="header-text">{match.date}</span>
                        </div>

                        <div className="team-row-all">
                            <div className="team-left">
                                <div className="logo-and-name">
                                    <img
                                        src={getTeamImageUrl(match.first_inning)}
                                        onError={(e) => { e.target.src = IMAGES.PHOENIX_LOGO; }}
                                        className="team-logo"
                                        alt={STRINGS.IMAGEALT}
                                    />
                                    <div className="team-name-allmatch">{match.first_inning.team_name}</div>
                                </div>
                                <div
                                    className={`team-score-all ${isOngoing ? 'blue-text' : 'gray-text'}`}
                                    style={{ display: "flex", alignItems: "center", gap: "10px", fontWeight: "500" }}
                                >
                                    {match.first_inning.total_runs}-{match.first_inning.total_wickets}
                                    <span className="team-over-all" style={{ fontWeight: "500" }}>
                                        ({match.first_inning.overs} Over)
                                    </span>
                                </div>
                            </div>

                            <div className="team-right">
                                <div className="logo-and-name-right">
                                    <img
                                        src={getTeamImageUrl(match.second_ining)}
                                        onError={(e) => { e.target.src = IMAGES.PHOENIX_LOGO; }}
                                        className="team-logo"
                                        alt={STRINGS.IMAGEALT}
                                    />
                                    <div className="team-name-allmatch">{match.second_ining.team_name}</div>
                                </div>
                                <span style={{ display: "flex", alignItems: "center", gap: "10px", justifyContent: "flex-end" }}>
                                    {!isSecondInningYetToBat && (
                                        <span className="team-score-all" style={{ fontWeight: "500" }}>
                                            {match.second_ining.total_runs}-{match.second_ining.total_wickets}
                                        </span>
                                    )}
                                    <span className="team-over-all" style={{ fontWeight: "500" }}>
                                        {formatOversLabel(match.second_ining.overs)}
                                    </span>
                                </span>
                            </div>
                        </div>
                        <div className={`footer ${isOngoing ? 'footer-live' : 'footer-centered'}`}>
                            <span>{match.match_message}</span>
                            {isOngoing && <span className="live-badge-footer">• LIVE</span>}
                        </div>
                    </div>
                );
            })}
            {isLoading && <div className="spinner-container pt-5"><div className="spinner"></div></div>}
            {!hasMore && allMatchData.length > 0 && <LoadMoreLoader />}
        </div>
    );
}

export default AllMatch;